import React, { useState, useMemo } from "react";
import { Collapse, Switch, Tooltip, Spin, message } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useAllPermissions, Permission } from "../../hooks/usePermissions"; // Updated import
import { useRoles } from "../../hooks/useRoles";
import { useToggleRolePermission } from "../../hooks/usePermissions";
import {
  PERMISSIONS,
  usePermissions as usePermissionContext,
  usePermissions,
} from "../../context/PermissionContext";

interface PermissionGroupProps {
  roleId: number;
}

const PermissionGroup: React.FC<PermissionGroupProps> = ({ roleId }) => {
  // Using the new hook that fetches all permissions
  const { data: permissions, isLoading: isPermissionsLoading } =
    useAllPermissions();
  const { hasPermission } = usePermissions();
  const { data: roles, isLoading: isRolesLoading } = useRoles();
  const { mutate: togglePermission, isPending } = useToggleRolePermission();
  const { refreshPermissions } = usePermissionContext();
  const canModifyPermissions = hasPermission(PERMISSIONS.MODIFY_PERMISSIONS);

  // Track local state for optimistic UI updates
  const [localPermissionState, setLocalPermissionState] = useState<
    Record<number, boolean>
  >({});

  // Get the current role and its permissions
  const role = useMemo(() => {
    return roles?.find((r) => r.id === roleId);
  }, [roles, roleId]);

  // Process permissions to get the latest status for each permission ID
  const permissionState = useMemo(() => {
    if (!role) return {};

    const permissionMap: Record<
      number,
      {
        rolePermissionId: number;
        status: boolean;
        updatedAt: string;
      }
    > = {};

    // Process all permissions to find the latest status for each permission ID
    role.permissions.forEach((permission) => {
      const permId = permission.permission_id;

      // If we haven't seen this permission yet, or this one is newer, update the map
      if (
        !permissionMap[permId] ||
        new Date(permission.updated_at) >
          new Date(permissionMap[permId].updatedAt)
      ) {
        permissionMap[permId] = {
          rolePermissionId: permission.id,
          status: permission.status === 1,
          updatedAt: permission.updated_at,
        };
      }
    });

    return permissionMap;
  }, [role]);

  // Group permissions by category - defined outside of conditional renders to avoid React Hook rules issues
  const permissionCategories = useMemo(() => {
    const categories: Record<string, Permission[]> = {};

    if (!permissions) return categories;

    permissions.forEach((permission) => {
      // Extract category from permission name (assuming naming convention like "Can View Users")
      const nameParts = permission.name.split(" ");
      if (nameParts.length >= 3) {
        // Get the last word which is typically the category (Users, Inventory, etc.)
        const category = nameParts[nameParts.length - 1];

        if (!categories[category]) {
          categories[category] = [];
        }

        categories[category].push(permission);
      } else {
        // If we can't determine a category, put in "Other"
        if (!categories["Other"]) {
          categories["Other"] = [];
        }
        categories["Other"].push(permission);
      }
    });

    return categories;
  }, [permissions]);

  const isLoading = isRolesLoading || isPermissionsLoading || !role;

  if (isLoading) return <Spin className="flex justify-center my-6" />;
  if (!permissions)
    return (
      <p className="text-primary text-center">Failed to load permissions</p>
    );

  const handleToggle = (permissionId: number) => {
    const permState = permissionState[permissionId];

    // Get current state (either from local overrides or from permission state)
    const currentStatus = permState
      ? localPermissionState[permissionId] !== undefined
        ? localPermissionState[permissionId]
        : permState.status
      : false;

    const newStatus = !currentStatus;

    // Update local state for immediate UI feedback
    setLocalPermissionState((prev) => ({
      ...prev,
      [permissionId]: newStatus,
    }));

    // If no existing permission state, create a new role-permission relationship
    if (!permState) {
      togglePermission(
        {
          role_id: roleId,
          permission_id: permissionId,
          status: newStatus ? 1 : 0,
          isNew: true, // Flag to indicate this is a new relationship
        },
        {
          onSuccess: () => {
            message.success("Permission updated successfully!");
            // Refresh permissions if needed
            checkAndRefreshUserPermissions();
          },
          onError: () => {
            // Revert the local state on error
            setLocalPermissionState((prev) => ({
              ...prev,
              [permissionId]: !newStatus,
            }));
            message.error("Failed to update permission. Please try again.");
          },
        }
      );
    } else {
      // Use the existing API call for updates
      togglePermission(
        {
          rolePermissionId: permState.rolePermissionId,
          role_id: roleId,
          permission_id: permissionId,
          status: newStatus ? 1 : 0,
        },
        {
          onSuccess: () => {
            message.success("Permission updated successfully!");
            checkAndRefreshUserPermissions();
          },
          onError: () => {
            // Revert the local state on error
            setLocalPermissionState((prev) => ({
              ...prev,
              [permissionId]: !newStatus,
            }));
            message.error("Failed to update permission. Please try again.");
          },
        }
      );
    }
  };

  // Extract the user permission refresh logic to avoid duplication
  const checkAndRefreshUserPermissions = () => {
    // Check if this is the current user's role and refresh permissions if needed
    const currentUserStr = localStorage.getItem("user");
    if (currentUserStr) {
      try {
        const currentUser = JSON.parse(currentUserStr);
        if (currentUser?.roleInfo?.userRole?.role_id === roleId) {
          // This is the current user's role, so refresh their permissions
          console.log("Refreshing current user's permissions after update");
          refreshPermissions();
        }
      } catch (err) {
        console.error("Error checking current user role:", err);
      }
    }
  };

  // Get the effective status for a permission (from local state if it exists, otherwise from API data)
  const getEffectiveStatus = (permissionId: number) => {
    if (localPermissionState[permissionId] !== undefined) {
      return localPermissionState[permissionId];
    }

    return permissionState[permissionId]?.status || false;
  };

  return (
    <Collapse bordered={false} defaultActiveKey={["2"]}>
      <Collapse.Panel
        header={<h3 className="font-semibold">Permissions</h3>}
        key="1"
      >
        {Object.entries(permissionCategories).length > 0 ? (
          <Collapse
            bordered={false}
            defaultActiveKey={Object.keys(permissionCategories)}
          >
            {Object.entries(permissionCategories).map(
              ([category, categoryPermissions]) => (
                <Collapse.Panel
                  header={<span className="font-medium">{category}</span>}
                  key={category}
                >
                  <div className="grid grid-cols-2 gap-4">
                    {categoryPermissions.map((permission) => (
                      <div
                        key={permission.id}
                        className="flex justify-between items-center ml-6"
                      >
                        <div className="flex items-center space-x-2">
                          <span>{permission.name}</span>
                          <Tooltip title={permission.description}>
                            <InfoCircleOutlined className="text-gray-400" />
                          </Tooltip>
                        </div>
                        <Switch
                          disabled={!canModifyPermissions}
                          checked={getEffectiveStatus(permission.id)}
                          loading={isPending}
                          onChange={() => handleToggle(permission.id)}
                        />
                      </div>
                    ))}
                  </div>
                </Collapse.Panel>
              )
            )}
          </Collapse>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {permissions.map((permission) => (
              <div
                key={permission.id}
                className="flex justify-between items-center ml-6"
              >
                <div className="flex items-center space-x-2">
                  <span>{permission.name}</span>
                  <Tooltip title={permission.description}>
                    <InfoCircleOutlined className="text-gray-400" />
                  </Tooltip>
                </div>
                <Switch
                  disabled={!canModifyPermissions}
                  checked={getEffectiveStatus(permission.id)}
                  loading={isPending}
                  onChange={() => handleToggle(permission.id)}
                />
              </div>
            ))}
          </div>
        )}
      </Collapse.Panel>
    </Collapse>
  );
};

export default PermissionGroup;
